import { useEffect, useState } from "react";

import type { GridSelectionStore } from "./grid-selection-store";

const MIN_SELECTION_LIMIT = 1;

export const normalizeSelectionLimit = (
  rawValue: string,
  totalMatching: number | null,
): number | null => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const floored = Math.floor(parsed);
  if (floored < MIN_SELECTION_LIMIT) {
    return null;
  }
  return totalMatching === null ? floored : Math.min(floored, totalMatching);
};

interface UseGridSelectionActionBarLimitArgs<TId extends string | number> {
  selection: GridSelectionStore<TId>;
  totalMatching: number | null;
}

export const useGridSelectionActionBarLimit = <TId extends string | number>({
  selection,
  totalMatching,
}: UseGridSelectionActionBarLimitArgs<TId>) => {
  const [selectAllLimitInput, setSelectAllLimitInput] = useState("");
  const isAllMatching = selection.mode === "all_matching";

  useEffect(() => {
    if (isAllMatching && selection.matchingLimit !== null) {
      setSelectAllLimitInput(String(selection.matchingLimit));
    }
    if (!isAllMatching) {
      setSelectAllLimitInput("");
    }
  }, [isAllMatching, selection.matchingLimit]);

  const normalizeLimit = (rawValue: string): number | null =>
    normalizeSelectionLimit(rawValue, totalMatching);

  const handleLimitInputChange = (value: string) => {
    setSelectAllLimitInput(value);
    if (!isAllMatching) {
      return;
    }
    const normalizedLimit = normalizeLimit(value);
    if (normalizedLimit !== null) {
      selection.setMatchingLimit(normalizedLimit);
    }
  };

  const handleSelectAllMatching = () => {
    if (totalMatching === null) {
      return;
    }
    const requestedLimit = normalizeLimit(selectAllLimitInput) ?? totalMatching;
    selection.setMatchingTotal(totalMatching);
    selection.switchToAllMatching(requestedLimit);
    setSelectAllLimitInput(String(requestedLimit));
  };

  const handleLimitInputBlur = () => {
    if (!isAllMatching || normalizeLimit(selectAllLimitInput) !== null) {
      return;
    }
    setSelectAllLimitInput(String(selection.matchingLimit ?? selection.count));
  };

  return {
    selectAllLimitInput,
    isAllMatching,
    handleLimitInputChange,
    handleSelectAllMatching,
    handleLimitInputBlur,
  };
};
